
import { Component,
  effect,
  EventEmitter,
  input,
  InputSignal,
  Output,
  signal,
  WritableSignal,
  ChangeDetectionStrategy } from '@angular/core';
import type { ComponentsReview, InputsReview, ProgramReview } from "@k12/models";
import { FilterValueArray, OrderByPipe } from "@k12/pipes";
import { NoticeComponent } from "@k12/ui-primitives";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [OrderByPipe, FilterValueArray, NoticeComponent],
  providers: [],

  selector: 'lib-pc-review',
  styleUrl: './pc-review.component.scss',
  templateUrl: './pc-review.component.html'
})
export class PcReviewComponent {
  // editing: InputSignal<boolean> = input(true);
  enrollmentBuilderView: InputSignal<boolean> = input(true);
  isAdmin: InputSignal<boolean> = input(false);
  isIneligible: InputSignal<boolean> = input(false);
  reviewData: InputSignal<ProgramReview> = input(<ProgramReview>{});

  @Output() editSectionOutput = new EventEmitter();

  public showBanner: WritableSignal<boolean> = signal(true);

  constructor() {
    effect(() => {
      this.showBanner.set(this.enrollmentBuilderView());
    });
  }

  public editSection(id: string, token: string) {
    this.editSectionOutput.emit({ id: id, token: token });
  }

  // Radio/checkbox store one boolean per option. Review has no componentType, so a component with
  // multiple boolean values is treated as that pattern: only show the selected (true) options.
  public visibleInputs(component: ComponentsReview): InputsReview[] {
    const inputs = component?.inputs ?? [];
    const booleanInputs = inputs.filter((input) => this.isBooleanValue(input.value));
    if (booleanInputs.length > 1) {
      return booleanInputs.filter((input) => this.isTrueValue(input.value));
    }
    return inputs;
  }

  // Skipped optional radio/checkbox groups (all false / no submitted inputs) have nothing to list.
  public hasNoResponse(component: ComponentsReview): boolean {
    return this.visibleInputs(component).length === 0;
  }

  // Display-only mapping; underlying review/API values stay "true"/"false".
  public formatDisplayValue(value: string | null | undefined): string {
    if (this.isTrueValue(value)) {
      return 'Yes';
    }
    if (this.isFalseValue(value)) {
      return 'No';
    }
    return value ?? '';
  }

  private isBooleanValue(value: string | null | undefined): boolean {
    return this.isTrueValue(value) || this.isFalseValue(value);
  }

  private isTrueValue(value: string | null | undefined): boolean {
    return String(value).trim().toLowerCase() === 'true';
  }

  private isFalseValue(value: string | null | undefined): boolean {
    return String(value).trim().toLowerCase() === 'false';
  }
}
